import { Controller } from "@hotwired/stimulus"

// Handles underlying fund investment forms with proper amount formatting
// Provides dual input system: formatted display input + hidden cents field
// Also auto-syncs initial NAV with initial funding
export default class extends Controller {
  static targets = [
    "commitmentDisplay", "commitmentHidden",
    "fundingDisplay", "fundingHidden",
    "navDisplay", "navHidden"
  ]

  connect() {
    this.formatInitialValues()
    // Track whether NAV was manually edited by user (vs auto-synced from funding)
    this.navManuallyEdited = false
  }

  // Format any existing values on connect
  formatInitialValues() {
    this.formatField("commitment")
    this.formatField("funding")
    this.formatField("nav")
  }

  formatField(fieldName) {
    const displayTarget = `${fieldName}DisplayTarget`
    const hiddenTarget = `${fieldName}HiddenTarget`

    if (this[`has${this.capitalize(fieldName)}DisplayTarget`] && this[`has${this.capitalize(fieldName)}HiddenTarget`]) {
      const cents = parseInt(this[hiddenTarget].value) || 0
      if (cents > 0) {
        this[displayTarget].value = this.formatNumber(cents / 100)
      }
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // Commitment amount changed
  commitmentChanged() {
    this.syncAmountField("commitment")
  }

  formatCommitment() {
    this.formatDisplayField("commitment")
  }

  // Initial funding amount changed
  fundingChanged() {
    this.syncAmountField("funding")
    // Auto-sync NAV to match funding if NAV is empty
    this.autoSyncNav()
  }

  formatFunding() {
    this.formatDisplayField("funding")
  }

  // NAV amount changed (manually by user)
  navChanged() {
    this.syncAmountField("nav")
    // Mark NAV as manually edited so auto-sync stops overriding it
    this.navManuallyEdited = true
  }

  formatNav() {
    this.formatDisplayField("nav")
  }

  // Sync display input to hidden cents field
  syncAmountField(fieldName) {
    const displayTarget = `${fieldName}DisplayTarget`
    const hiddenTarget = `${fieldName}HiddenTarget`

    if (this[`has${this.capitalize(fieldName)}DisplayTarget`] && this[`has${this.capitalize(fieldName)}HiddenTarget`]) {
      const displayValue = this[displayTarget].value
      const dollars = this.parseNumber(displayValue)
      const cents = Math.round(dollars * 100)
      this[hiddenTarget].value = cents > 0 ? cents : ""
    }
  }

  // Format display field on blur
  formatDisplayField(fieldName) {
    const displayTarget = `${fieldName}DisplayTarget`

    if (this[`has${this.capitalize(fieldName)}DisplayTarget`]) {
      const displayValue = this[displayTarget].value
      const dollars = this.parseNumber(displayValue)
      if (dollars > 0) {
        this[displayTarget].value = this.formatNumber(dollars)
      }
    }
  }

  // Auto-sync initial NAV to match initial funding when user enters funding
  autoSyncNav() {
    if (!this.hasNavDisplayTarget || !this.hasNavHiddenTarget) return
    if (!this.hasFundingHiddenTarget) return

    // Don't override if user has manually edited NAV
    if (this.navManuallyEdited) return

    const fundingCents = parseInt(this.fundingHiddenTarget.value) || 0
    if (fundingCents > 0) {
      this.navHiddenTarget.value = fundingCents
      this.navDisplayTarget.value = this.formatNumber(fundingCents / 100)
    } else {
      // Clear NAV if funding is cleared
      this.navHiddenTarget.value = ""
      this.navDisplayTarget.value = ""
    }
  }

  // Format number with commas (e.g., 1,000,000)
  formatNumber(num) {
    return Math.round(num).toLocaleString("en-US")
  }

  // Parse number from formatted string
  parseNumber(str) {
    if (!str) return 0
    return parseFloat(str.replace(/[^0-9.]/g, "")) || 0
  }
}
